import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Plus, RefreshCw, Search, SlidersHorizontal } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, DataTable, EmptyState, ui } from "../../../components/ui";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { cn } from "../../../lib/utils";
import { hasCanonicalRole } from "../../../lib/auth";
import {
  fetchCurrencyOptions,
  fetchLeads,
  fetchLeadsByStatus,
  fetchMyLeads,
  fetchUnassignedLeads,
  getCustomer,
  getEmployee,
  searchLeads,
} from "../../../services/leadManagementService";
import { canCreateLead, formatAmount } from "../leadForm";
import { LeadStatusBadge } from "../components/LeadStatus";

const PAGE_SIZE = 10;

/** Saved filters: each one is its own endpoint. The user's own leads come first. */
const FILTERS = [
  { id: "mine", labelKey: "leadManagement.filtersMine", read: fetchMyLeads },
  { id: "unassigned", labelKey: "leadManagement.filtersUnassigned", read: fetchUnassignedLeads },
  { id: "Lost", labelKey: "leadManagement.filtersLost", read: (paging) => fetchLeadsByStatus("Lost", paging) },
  { id: "Won", labelKey: "leadManagement.filtersWon", read: (paging) => fetchLeadsByStatus("Won", paging) },
  { id: "On_Hold", labelKey: "leadManagement.filtersOnHold", read: (paging) => fetchLeadsByStatus("On_Hold", paging) },
];

/** Every lead, whoever created it — GET /saas/crm/leads/search. */
const ALL_LEADS = { id: "all", labelKey: "leadManagement.filtersAll", read: fetchLeads };

function SkeletonRows() {
  return (
    <div className="divide-y divide-(--color-text-border)/35">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex animate-pulse items-center gap-4 px-4 py-4">
          <div className="h-3 w-16 rounded bg-workspace" />
          <div className="h-3 flex-1 rounded bg-workspace" />
          <div className="hidden h-3 w-32 rounded bg-workspace sm:block" />
          <div className="h-5 w-16 rounded-full bg-workspace" />
        </div>
      ))}
    </div>
  );
}

/** Lead Management landing: the user's own leads, with the saved filters. */
export function LeadsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [flash] = useState(location.state?.flash ?? "");
  // A Service Manager starts on every lead; everyone else on their own.
  const [filters] = useState(() => (hasCanonicalRole("Service_Manager") ? [ALL_LEADS, ...FILTERS] : FILTERS));
  const [filter, setFilter] = useState(filters[0]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [termInput, setTermInput] = useState("");
  const [term, setTerm] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ by: "createdAt", dir: "DESC" });

  const [result, setResult] = useState({ rows: [], totalItems: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [currencies, setCurrencies] = useState([]);
  const [customerNames, setCustomerNames] = useState({});
  const [employeeNames, setEmployeeNames] = useState({});

  useEffect(() => {
    if (location.state?.flash) navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    fetchCurrencyOptions().then(setCurrencies);
  }, []);

  const load = useCallback(() => {
    const paging = { page: page - 1, size: PAGE_SIZE, sortBy: sort.by, sortDir: sort.dir };
    setLoading(true);
    setLoadError("");
    (term ? searchLeads(term, paging) : filter.read(paging))
      .then(setResult)
      .catch((error) => {
        setResult({ rows: [], totalItems: 0, totalPages: 1 });
        setLoadError(error.message);
      })
      .finally(() => setLoading(false));
  }, [filter, term, page, sort]);

  useEffect(() => load(), [load]);

  // The rows carry ids; name each customer and primary sales executive on the page once.
  useEffect(() => {
    [...new Set(result.rows.map((lead) => lead.customerId))]
      .filter((id) => id && !customerNames[id])
      .forEach((id) => getCustomer(id).then((row) => setCustomerNames((current) => ({ ...current, [id]: row.name }))));
    [...new Set(result.rows.filter((lead) => !lead.primarySalesExecutiveName).map((lead) => lead.primarySalesExecutiveId))]
      .filter((id) => id && !employeeNames[id])
      .forEach((id) => getEmployee(id).then((row) => setEmployeeNames((current) => ({ ...current, [id]: row.name }))));
  }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilter = (next) => {
    setFilter(next);
    setTerm("");
    setTermInput("");
    setPage(1);
  };

  const applySearch = (event) => {
    event.preventDefault();
    setFilter(filters[0]);
    setTerm(termInput.trim());
    setPage(1);
  };

  const toggleSort = (by) => {
    setSort((current) => ({ by, dir: current.by === by && current.dir === "ASC" ? "DESC" : "ASC" }));
    setPage(1);
  };

  /** The row's primary sales executive: as the row names them, or as looked up by id. */
  const salesExecutiveOf = (lead) => lead.primarySalesExecutiveName || employeeNames[lead.primarySalesExecutiveId];
  const isAssigned = (lead) => Boolean(lead.primarySalesExecutiveId || lead.primarySalesExecutiveName);

  const sortHeader = (label, by) => {
    const Icon = sort.by !== by ? ArrowUpDown : sort.dir === "ASC" ? ArrowUp : ArrowDown;
    return (
      <button type="button" onClick={() => toggleSort(by)} className="inline-flex items-center gap-1 uppercase hover:text-primary">
        {label}
        <Icon className="h-3 w-3" />
      </button>
    );
  };

  const valueOf = (lead) =>
    formatAmount(lead.projectValueAmount, currencies.find((row) => row.id === lead.projectValueMetric)?.label);

  const open = (lead) => navigate(`/crm/lead-management/leads/${lead.leadId}`);

  const columns = [
    { key: "leadId", label: sortHeader(t("leadManagement.fieldsLeadId"), "leadId") },
    { key: "projectName", label: sortHeader(t("leadManagement.fieldsProjectName"), "projectName") },
    { key: "customer", label: t("leadManagement.fieldsCustomerId") },
    { key: "status", label: sortHeader(t("leadManagement.fieldsLeadStatus"), "leadStatus") },
    { key: "pse", label: t("leadManagement.fieldsPrimarySalesExecutiveId") },
    { key: "value", label: sortHeader(t("leadManagement.fieldsProjectValueAmount"), "projectValueAmount"), className: "text-right" },
  ];

  const createButton = canCreateLead() ? (
    <button type="button" onClick={() => navigate("/crm/lead-management/leads/create")} className={ui.primaryBtn}>
      <Plus className="h-4 w-4" />
      {t("leadManagement.create")}
    </button>
  ) : null;

  const unfiltered = filter === filters[0] && !term;

  return (
    <AppLayout>

      <div className="mb-5 flex items-end justify-between gap-3">
        <PageTitleRow back={<PageBackLink to="/" />}>
          <h1 className={ui.pageTitle}>{t("leadManagement.title")}</h1>
        </PageTitleRow>
        <div className="hidden sm:block">{createButton}</div>
      </div>

      {flash ? <p className="mb-3 text-[12px] font-semibold text-emerald-700">{flash}</p> : null}

      {loadError ? (
        <div className="mb-4 flex flex-col gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 self-start rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 transition hover:bg-rose-100 sm:self-auto"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("leadManagement.retry")}
          </button>
        </div>
      ) : null}

      {/* Search with the Filter button beside it; on a phone Create Lead follows below. */}
      <div className="mb-4 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex items-center gap-2.5">
          <form onSubmit={applySearch} className="relative w-full sm:w-[280px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 per-file-hint" />
            <input data-page-search="true"
              type="search"
              value={termInput}
              onChange={(event) => setTermInput(event.target.value)}
              placeholder={t("leadManagement.searchPlaceholder")}
              className={cn(ui.input, "h-9 w-full pl-9")}
            />
          </form>

          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(ui.filterBtn, "shrink-0", filter !== filters[0] && !term && "border-primary text-primary")}
              >
                <SlidersHorizontal className="h-4 w-4" />
                {t("leadManagement.filtersButton")}
                {filter !== filters[0] && !term ? (
                  <span className="rounded-md bg-primary px-1.5 py-0.5 text-[11px] text-white">{t(filter.labelKey)}</span>
                ) : null}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-48 rounded-xl border-(--color-text-border) bg-white p-1.5">
              {filters.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    applyFilter(option);
                    setFilterOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition hover:bg-workspace/40",
                    filter === option && !term ? "font-semibold text-primary" : "text-foreground"
                  )}
                >
                  {t(option.labelKey)}
                  {filter === option && !term ? <Check className="h-4 w-4" /> : null}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>

        <div className="sm:hidden [&>button]:w-full">{createButton}</div>
      </div>

      <ContentCard>
        {loading ? (
          <SkeletonRows />
        ) : result.rows.length === 0 ? (
          <div>
            <EmptyState
              title={unfiltered ? t("leadManagement.emptyTitle") : t("leadManagement.emptyFilteredTitle")}
              message={unfiltered ? t("leadManagement.emptyMessage") : t("leadManagement.emptyFilteredMessage")}
            />
          </div>
        ) : (
          <>
            <DataTable columns={columns} minWidth="900px">
              {result.rows.map((lead) => (
                <tr key={lead.leadId} onClick={() => open(lead)} className="cursor-pointer transition hover:bg-workspace/20">
                  <td className="px-4 py-3 font-mono text-[12px] font-semibold text-foreground">{lead.leadId}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">{lead.projectName}</td>
                  <td className="px-4 py-3 per-file-hint">{customerNames[lead.customerId]}</td>
                  <td className="px-4 py-3">
                    <LeadStatusBadge status={lead.leadStatus} />
                  </td>
                  <td className="px-4 py-3 per-file-hint">
                    {isAssigned(lead) ? (
                      salesExecutiveOf(lead)
                    ) : (
                      <span className="italic">{t("leadManagement.unassigned")}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground">{valueOf(lead)}</td>
                </tr>
              ))}
            </DataTable>

            <div className="divide-y divide-(--color-text-border)/40 md:hidden">
              {result.rows.map((lead) => (
                <button
                  type="button"
                  key={lead.leadId}
                  onClick={() => open(lead)}
                  className="flex w-full items-start justify-between gap-3 p-4 text-left transition hover:bg-workspace/20"
                >
                  <div className="min-w-0">
                    <p className="font-mono text-[11px] per-file-hint">{lead.leadId}</p>
                    <p className="truncate text-sm font-bold text-foreground">{lead.projectName}</p>
                    <p className="mt-0.5 truncate text-[12px] per-file-hint">
                      {t("leadManagement.fieldsCustomerId")}: <span className="text-foreground">{customerNames[lead.customerId]}</span>
                    </p>
                    <p className="mt-2 text-[11px] per-file-hint">
                      {isAssigned(lead) ? salesExecutiveOf(lead) : t("leadManagement.unassigned")}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end justify-between gap-3 self-stretch">
                    <LeadStatusBadge status={lead.leadStatus} />
                    <span className="text-[12px] font-semibold text-foreground">{valueOf(lead)}</span>
                  </div>
                </button>
              ))}
            </div>

            <TablePagination
              page={page}
              totalPages={result.totalPages}
              totalItems={result.totalItems}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>
    </AppLayout>
  );
}

export default LeadsPage;
